import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { IAddress, StorageKey } from '@/client/types'
import {
  getUserAddresses,
  createUserAddress,
  updateUserAddress,
  isActiveAddress,
  validateAddress,
  getAddressManagementInfos
} from '@/infra/services'

type argsMutate = {
  endpoint: string
  address: IAddress
}

export const useGetUserAddresses = () =>
  useQuery({
    queryKey: ['userAddresses'],
    queryFn: () => getUserAddresses(),
    refetchOnWindowFocus: false
  })

export const useMutateCreateUserAddress = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ endpoint, address }: argsMutate) => createUserAddress(endpoint, address),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.USER_ADDRESS] })
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['addressErrorResult'] })
    }
  })
}

export const useMutateUpdateUserAddress = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ endpoint, address }: argsMutate) => updateUserAddress(endpoint, address),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.USER_ADDRESS] })
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['addressEditErrorResult'] })
    }
  })
}

/**
 * Retrieve the active status of the address
 */
export const useMutateIsActiveAddress = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (address: any) => isActiveAddress(address),
    onSuccess: (data) => data,
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['isActiveAddresses'] })
    }
  })
}

export const useMutateValidateAddress = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (address: any) => validateAddress(address),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [StorageKey.VALID_ADDRESS] })
    }
  })
}

export const useGetAddressManagementInfos = (locale: string = 'es') =>
  useQuery({
    queryKey: [StorageKey.ADDRESS_MANAGEMENT_INFOS, locale],
    queryFn: () => getAddressManagementInfos(locale),
    enabled: !!locale,
    staleTime: Infinity,
    placeholderData: keepPreviousData
  })
